import { useEffect, useState } from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Search, X } from 'lucide-react';
import { getVimeoContent } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface ContentItem {
  id: string;
  title: string;
  author?: string;
  duration?: string;
  mediaType: string;
  modules?: string[];
  contentLibraries?: string[];
  contentCategories?: string[];
  thumbnailUrl?: string;
  externalId: string;
  enabled: boolean;
}

interface VideoContentSelectorProps {
  selectedContent: ContentItem[];
  onSelectionChange: (selected: ContentItem[]) => void;
  contentLibrary?: string;
}

export const parseDuration = (durationStr?: string): number => {
  if (!durationStr) return 0;
  
  const parts = durationStr.split(':');
  if (parts.length !== 3) return 0;
  
  const hours = parseInt(parts[0], 10) || 0;
  const minutes = parseInt(parts[1], 10) || 0;
  const seconds = parseFloat(parts[2]) || 0;
  
  // Round up to ensure partial minutes are counted as full minutes
  return Math.ceil(hours * 60 + minutes + seconds / 60);
};

export default function VideoContentSelector({ selectedContent, onSelectionChange, contentLibrary }: VideoContentSelectorProps) {
  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [libraryFilter, setLibraryFilter] = useState('all'); // Start with 'all' to show all videos
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [categories, setCategories] = useState<string[]>([]);
  const [libraries, setLibraries] = useState<string[]>([]);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    setLoading(true);
    try {
      logger.log('🎬 VideoContentSelector: Loading content...');
      const items = await getVimeoContent() as ContentItem[];
      
      logger.log('🎬 VideoContentSelector: Loaded items:', items.length);
      setContentItems(items);
      
      const categoriesSet = new Set(
        items.flatMap(item => item.contentCategories || []).filter((cat): cat is string => Boolean(cat))
      );
      const librariesSet = new Set(
        items.flatMap(item => item.contentLibraries || []).filter((lib): lib is string => Boolean(lib))
      );
      const uniqueCategories = Array.from(categoriesSet);
      const uniqueLibraries = Array.from(librariesSet);
      
      logger.log('🎬 VideoContentSelector: Categories:', uniqueCategories.length, 'Libraries:', uniqueLibraries.length);
      setCategories(uniqueCategories);
      setLibraries(uniqueLibraries);
    } catch (error) {
      logger.error('Error loading content:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = contentItems;
    logger.log('🎬 Filtering:', { 
      total: contentItems.length, 
      searchTerm, 
      moduleFilter, 
      libraryFilter, 
      categoryFilter 
    });

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.author?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (moduleFilter && moduleFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.modules?.includes(moduleFilter.toLowerCase()) || 
        item.modules?.includes(moduleFilter)
      );
    }

    if (libraryFilter && libraryFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.contentLibraries?.includes(libraryFilter)
      );
    }

    if (categoryFilter && categoryFilter !== 'all') {
      filtered = filtered.filter(item => 
        item.contentCategories?.includes(categoryFilter)
      );
    }

    logger.log('🎬 Filtered items:', filtered.length);
    setFilteredItems(filtered);
  }, [contentItems, searchTerm, moduleFilter, libraryFilter, categoryFilter]);

  const isSelected = (contentId: string) => {
    return selectedContent.some(item => item.id === contentId);
  };

  const toggleContentSelection = (content: ContentItem) => {
    const isCurrentlySelected = isSelected(content.id);
    
    if (isCurrentlySelected) {
      const updatedSelection = selectedContent.filter(item => item.id !== content.id);
      onSelectionChange(updatedSelection);
    } else {
      const contentWithParsedDuration = {
        ...content,
        duration: parseDuration(content.duration)
      };
      const updatedSelection = [...selectedContent, contentWithParsedDuration as any];
      onSelectionChange(updatedSelection);
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setModuleFilter('all');
    setLibraryFilter('all');
    setCategoryFilter('all');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Badge variant="default" className="px-3 py-1">
          {selectedContent.length} videos selected
        </Badge>
        {(searchTerm || moduleFilter !== 'all' || libraryFilter !== 'all' || categoryFilter !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={clearFilters}
            data-testid="button-clear-filters"
          >
            <X className="h-4 w-4 mr-2" />
            Clear Filters
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
          <Input
            placeholder="Search videos..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="input-search"
          />
        </div>

        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger data-testid="select-module-filter">
            <SelectValue placeholder="All Modules" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Modules</SelectItem>
            <SelectItem value="mind">Mind</SelectItem>
            <SelectItem value="body">Body</SelectItem>
          </SelectContent>
        </Select>

        <Select value={libraryFilter} onValueChange={setLibraryFilter}>
          <SelectTrigger data-testid="select-library-filter">
            <SelectValue placeholder="All Libraries" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Libraries</SelectItem>
            {libraries.map((lib) => (
              <SelectItem key={lib} value={lib}>
                {lib}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger data-testid="select-category-filter">
            <SelectValue placeholder="All Categories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat}>
                {cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <p className="text-slate-500">Loading videos...</p>
        </div>
      ) : filteredItems.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-slate-500">No videos found matching your filters.</p>
        </div>
      ) : (
        <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-2">
          {filteredItems.map((content) => (
            <div
              key={content.id}
              className={`p-3 border rounded-md hover:bg-slate-50 transition-colors ${
                isSelected(content.id) ? 'bg-blue-50 border-blue-200' : 'border-slate-200'
              }`}
            >
              <div className="flex items-center space-x-3">
                <Checkbox
                  data-testid={`checkbox-content-${content.id}`}
                  checked={isSelected(content.id)}
                  onCheckedChange={() => toggleContentSelection(content)}
                />
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium text-slate-900 truncate text-sm">{content.title}</h4>
                  <div className="flex items-center space-x-3 text-xs text-slate-500 mt-1">
                    {content.author && <span>by {content.author}</span>}
                    {content.duration && <span>{parseDuration(content.duration)} min</span>}
                    {content.modules && content.modules.length > 0 && (
                      <div className="flex gap-1">
                        {content.modules.map(module => (
                          <Badge key={module} variant="outline" className="text-xs">
                            {module}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
